import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, formatDate, scr } from '../../lib/api';
import { sha256Hex } from '../../lib/hash';
import { useAuth } from '../../lib/auth';
import type { ProjectDocument, ProjectMilestone, ProjectTracker } from '../../lib/types';
import { useApiQuery, useCursorList } from '../../lib/useApi';
import { BackLink, EmptyState, ErrorNote, Field, LoadMoreButton, Modal, PageHeader, Spinner, StatCard, StatusBadge } from '../../ui/components';

const CATEGORIES = ['ROAD', 'WATER', 'ELECTRICITY', 'SEWERAGE', 'BUILDING', 'COASTAL_PROTECTION', 'JETTY_MARINE', 'OTHER'] as const;
const STATUSES = ['PLANNED', 'PROCUREMENT', 'IN_PROGRESS', 'ON_HOLD', 'COMPLETED', 'CANCELLED'] as const;
const DOC_TYPES = ['PROJECT_PLAN', 'CONTRACT', 'ENVIRONMENTAL_ASSESSMENT', 'OTHER'] as const;
const ALLOWED_NEXT: Record<string, string[]> = {
  PLANNED: ['PROCUREMENT', 'ON_HOLD', 'CANCELLED'],
  PROCUREMENT: ['IN_PROGRESS', 'ON_HOLD', 'CANCELLED'],
  IN_PROGRESS: ['COMPLETED', 'ON_HOLD', 'CANCELLED'],
  ON_HOLD: ['IN_PROGRESS', 'PROCUREMENT', 'CANCELLED'],
  COMPLETED: [],
  CANCELLED: [],
};

export function Projects() {
  const { persona } = useAuth();
  const isPm = persona?.roles.includes('PROJECT_PM') ?? false;
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [district, setDistrict] = useState('');
  const [q, setQ] = useState('');
  const [showNew, setShowNew] = useState(false);

  const basePath = useMemo(() => {
    const params = new URLSearchParams();
    params.set('limit', '25');
    if (status) params.set('status', status);
    if (category) params.set('category', category);
    if (district) params.set('district', district);
    if (q.trim()) params.set('q', q.trim());
    return `/api/v1/m20/projects?${params.toString()}`;
  }, [status, category, district, q]);
  const list = useCursorList<ProjectTracker>(basePath);

  return (
    <div>
      <PageHeader
        title="Infrastructure Projects"
        subtitle="Public projects dashboard — milestones + spend feeding C11 OpenGov transparency."
        actions={isPm ? <button className="btn-primary" onClick={() => setShowNew(true)}>+ Register project</button> : undefined}
      />

      <div className="card mb-4 flex flex-wrap items-end gap-3 p-4">
        <Field label="Status" className="w-44">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replaceAll('_', ' ')}</option>)}
          </select>
        </Field>
        <Field label="Category" className="w-44">
          <select className="field-input" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">All categories</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c.replaceAll('_', ' ')}</option>)}
          </select>
        </Field>
        <Field label="District" className="w-40">
          <input className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)} />
        </Field>
        <Field label="Search" className="min-w-[180px] flex-1">
          <input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} />
        </Field>
      </div>

      {list.error ? <div className="mb-4"><ErrorNote message={list.error} /></div> : null}

      <div className="card overflow-x-auto">
        <table className="w-full min-w-[720px] border-collapse">
          <thead>
            <tr>
              <th className="table-head">Ref no</th>
              <th className="table-head">Name</th>
              <th className="table-head">Category</th>
              <th className="table-head">District</th>
              <th className="table-head">Budget</th>
              <th className="table-head">Spent</th>
              <th className="table-head">Status</th>
            </tr>
          </thead>
          <tbody>
            {list.items.map((p) => (
              <tr key={p.id} className="table-row-link" onClick={() => navigate(`/infrastructure/projects/${p.id}`)}>
                <td className="table-cell font-semibold text-slate-800">{p.refNo}</td>
                <td className="table-cell">{p.name}</td>
                <td className="table-cell text-xs">{p.category.replaceAll('_', ' ')}</td>
                <td className="table-cell">{p.district ?? '—'}</td>
                <td className="table-cell">{scr(p.budgetScr)}</td>
                <td className="table-cell">{scr(p.spentScr)}</td>
                <td className="table-cell"><StatusBadge status={p.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.loading ? <Spinner label="Loading projects…" /> : null}
        {!list.loading && list.items.length === 0 ? <EmptyState title="No infrastructure projects found" /> : null}
      </div>
      <LoadMoreButton nextCursor={list.nextCursor} loading={list.loading} onClick={list.loadMore} />

      {showNew ? (
        <NewProjectModal onClose={() => setShowNew(false)} onCreated={(p) => { setShowNew(false); navigate(`/infrastructure/projects/${p.id}`); }} />
      ) : null}
    </div>
  );
}

function NewProjectModal({ onClose, onCreated }: { onClose: () => void; onCreated: (p: ProjectTracker) => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]>('ROAD');
  const [district, setDistrict] = useState('');
  const [budgetScr, setBudgetScr] = useState('');
  const [fundingSource, setFundingSource] = useState('');
  const [startDate, setStartDate] = useState('');
  const [plannedEndDate, setPlannedEndDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<ProjectTracker>('/api/v1/m20/projects', {
        name,
        description: description || undefined,
        category,
        district: district || undefined,
        budgetScr: Number(budgetScr),
        fundingSource: fundingSource || undefined,
        startDate: startDate || undefined,
        plannedEndDate: plannedEndDate || undefined,
      });
      onCreated(created);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not register project');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register infrastructure project" onClose={onClose} wide>
      <p className="mb-3 text-xs text-slate-500">Publishes an OpenGov gazette notice on creation.</p>
      {error ? <div className="mb-3"><ErrorNote message={error} /></div> : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name" className="sm:col-span-2">
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Category">
          <select className="field-input" value={category} onChange={(e) => setCategory(e.target.value as typeof category)}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c.replaceAll('_', ' ')}</option>)}
          </select>
        </Field>
        <Field label="District">
          <input className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)} />
        </Field>
        <Field label="Budget (SCR)">
          <input className="field-input" type="number" min="0" value={budgetScr} onChange={(e) => setBudgetScr(e.target.value)} />
        </Field>
        <Field label="Funding source">
          <input className="field-input" value={fundingSource} onChange={(e) => setFundingSource(e.target.value)} />
        </Field>
        <Field label="Start date">
          <input className="field-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </Field>
        <Field label="Planned end date">
          <input className="field-input" type="date" value={plannedEndDate} onChange={(e) => setPlannedEndDate(e.target.value)} />
        </Field>
        <Field label="Description" className="sm:col-span-2">
          <textarea className="field-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={busy || !name || !budgetScr} onClick={submit}>
          {busy ? 'Registering…' : 'Register project'}
        </button>
      </div>
    </Modal>
  );
}

export function ProjectDetail() {
  const { id = '' } = useParams();
  const { persona } = useAuth();
  const isPm = persona?.roles.includes('PROJECT_PM') ?? false;
  const projectQ = useApiQuery<ProjectTracker & { milestones: ProjectMilestone[]; documents: ProjectDocument[] }>(`/api/v1/m20/projects/${id}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [milestoneTitle, setMilestoneTitle] = useState('');
  const [milestonePlannedDate, setMilestonePlannedDate] = useState('');
  const [spendFor, setSpendFor] = useState<string | null>(null);
  const [spendAmount, setSpendAmount] = useState('');
  const [docType, setDocType] = useState<(typeof DOC_TYPES)[number]>('PROJECT_PLAN');
  const [docRef, setDocRef] = useState('');

  const act = async (fn: () => Promise<unknown>, message?: string) => {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await fn();
      projectQ.reload();
      if (message) setNote(message);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  if (projectQ.loading) return <Spinner label="Loading project…" />;
  if (projectQ.error || !projectQ.data) return <ErrorNote message={projectQ.error ?? 'Project not found'} />;
  const p = projectQ.data;
  const next = ALLOWED_NEXT[p.status] ?? [];

  return (
    <div>
      <BackLink to="/infrastructure/projects" />
      <PageHeader title={p.name} subtitle={`${p.refNo} · ${p.category.replaceAll('_', ' ')}${p.district ? ` · ${p.district}` : ''}`} actions={<StatusBadge status={p.status} />} />
      {error ? <div className="mb-4"><ErrorNote message={error} /></div> : null}
      {note ? <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{note}</div> : null}

      <div className="mb-5 grid gap-3 sm:grid-cols-3">
        <StatCard label="Budget" value={scr(p.budgetScr)} />
        <StatCard label="Spent" value={scr(p.spentScr)} tone={p.spentScr > p.budgetScr ? 'bad' : 'default'} />
        <StatCard label="Funding source" value={p.fundingSource ?? '—'} />
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <div className="card p-5">
            <h2 className="mb-2 text-sm font-bold text-slate-700">Description</h2>
            <p className="text-sm text-slate-600">{p.description ?? '—'}</p>
            {isPm && next.length > 0 ? (
              <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
                {next.map((s) => (
                  <button key={s} className="btn-secondary" disabled={busy} onClick={() => act(() => api.patch(`/api/v1/m20/projects/${id}`, { status: s }), `Status moved to ${s}.`)}>
                    Move to {s.replaceAll('_', ' ')}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="card p-5">
            <h2 className="mb-3 text-sm font-bold text-slate-700">Milestones</h2>
            <div className="mb-3 space-y-2">
              {p.milestones.map((m) => (
                <div key={m.id} className="rounded-lg border border-slate-200 p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-800">{m.title}</span>
                    <StatusBadge status={m.status} />
                  </div>
                  <div className="mt-1 text-xs text-slate-500">Planned {formatDate(m.plannedDate)}{m.spendRecordedScr !== null ? ` · spend ${scr(m.spendRecordedScr)}` : ''}</div>
                  {isPm && m.status === 'PENDING' ? (
                    spendFor === m.id ? (
                      <div className="mt-2 flex gap-2 border-t border-slate-100 pt-2">
                        <input className="field-input" type="number" min="0" placeholder="Spend (SCR)" value={spendAmount} onChange={(e) => setSpendAmount(e.target.value)} />
                        <button
                          className="btn-primary btn-xs shrink-0"
                          disabled={busy || !spendAmount}
                          onClick={() => act(() => api.post(`/api/v1/m20/projects/${id}/milestones/${m.id}/complete`, { spendScr: Number(spendAmount) }), 'Milestone met.').then(() => setSpendFor(null))}
                        >
                          Confirm met
                        </button>
                      </div>
                    ) : (
                      <div className="mt-2 flex gap-2">
                        <button className="btn-secondary btn-xs" onClick={() => setSpendFor(m.id)}>Mark met</button>
                        <button className="btn-danger btn-xs" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m20/projects/${id}/milestones/${m.id}/miss`), 'Milestone missed.')}>Mark missed</button>
                      </div>
                    )
                  ) : null}
                </div>
              ))}
              {p.milestones.length === 0 ? <EmptyState title="No milestones yet" /> : null}
            </div>
            {isPm && !['COMPLETED', 'CANCELLED'].includes(p.status) ? (
              <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 pt-3">
                <Field label="Title" className="min-w-[160px] flex-1">
                  <input className="field-input" value={milestoneTitle} onChange={(e) => setMilestoneTitle(e.target.value)} />
                </Field>
                <Field label="Planned date">
                  <input className="field-input" type="date" value={milestonePlannedDate} onChange={(e) => setMilestonePlannedDate(e.target.value)} />
                </Field>
                <button
                  className="btn-secondary"
                  disabled={busy || !milestoneTitle || !milestonePlannedDate}
                  onClick={() => act(() => api.post(`/api/v1/m20/projects/${id}/milestones`, { title: milestoneTitle, plannedDate: milestonePlannedDate }), 'Milestone added.').then(() => { setMilestoneTitle(''); setMilestonePlannedDate(''); })}
                >
                  Add milestone
                </button>
              </div>
            ) : null}
          </div>

          <div className="card p-5">
            <h2 className="mb-3 text-sm font-bold text-slate-700">Documents (DocVault-issued)</h2>
            <ul className="mb-3 space-y-1 text-xs text-slate-600">
              {p.documents.map((d) => (
                <li key={d.id} className="flex justify-between border-b border-slate-100 py-1">
                  <span>{d.docType.replaceAll('_', ' ')}</span>
                  <span className="font-mono text-slate-400">{d.docSerial}</span>
                </li>
              ))}
              {p.documents.length === 0 ? <li className="text-slate-400">No documents attached</li> : null}
            </ul>
            {isPm ? (
              <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 pt-3">
                <Field label="Doc type" className="w-56">
                  <select className="field-input" value={docType} onChange={(e) => setDocType(e.target.value as typeof docType)}>
                    {DOC_TYPES.map((t) => <option key={t} value={t}>{t.replaceAll('_', ' ')}</option>)}
                  </select>
                </Field>
                <Field label="Doc reference" className="min-w-[180px] flex-1">
                  <input className="field-input" value={docRef} onChange={(e) => setDocRef(e.target.value)} />
                </Field>
                <button
                  className="btn-secondary"
                  disabled={busy || !docRef}
                  onClick={() => act(async () => {
                    const sha256 = await sha256Hex(docRef);
                    await api.post(`/api/v1/m20/projects/${id}/documents`, { docType, docRef, sha256 });
                  }, 'Document issued via DocVault.').then(() => setDocRef(''))}
                >
                  Issue document
                </button>
              </div>
            ) : null}
          </div>
        </div>

        <div className="space-y-5">
          <div className="card p-5">
            <h2 className="mb-2 text-sm font-bold text-slate-700">Timeline</h2>
            <dl className="space-y-1.5 text-xs">
              <div className="flex justify-between"><dt className="text-slate-500">Start</dt><dd>{p.startDate ? formatDate(p.startDate) : '—'}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Planned end</dt><dd>{p.plannedEndDate ? formatDate(p.plannedEndDate) : '—'}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Actual end</dt><dd>{p.actualEndDate ? formatDate(p.actualEndDate) : '—'}</dd></div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}
